import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from "recharts";

const COLORS = ["#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#84CC16"];

const KmDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState("");
  
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  useEffect(() => {
    fetchData();
    if (isSuperAdmin) {
      fetchTenants();
    }
  }, [selectedYear, selectedTenant]);

  const fetchTenants = async () => {
    try {
      const res = await api.get("/tenants");
      setTenants(res.data);
    } catch (error) {
      console.error("Error fetching tenants:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("year", selectedYear);
      if (selectedTenant) params.append("tenant_id", selectedTenant);
      
      const res = await api.get(`/hr/km-maps/dashboard?${params.toString()}`);
      setData(res.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Erro ao carregar dashboard");
    } finally {
      setLoading(false);
    }
  };

  const formatKm = (value) => `${value.toLocaleString("pt-PT")} km`;
  
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border rounded-lg shadow-lg p-3">
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString("pt-PT") : entry.value}
              {entry.dataKey.includes("km") ? " km" : ""}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statusData = data?.status_distribution ? [
    { name: "Aprovados", value: data.status_distribution.approved, color: "#10B981" },
    { name: "Pendentes", value: data.status_distribution.pending, color: "#F59E0B" },
    { name: "Rejeitados", value: data.status_distribution.rejected, color: "#EF4444" }
  ] : [];

  return (
    <div data-testid="km-dashboard-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Dashboard de Quilómetros</h1>
          <p className="text-muted-foreground">Análise e estatísticas de KM por mês e colaborador</p>
        </div>
        <div className="flex items-center gap-3">
          {isSuperAdmin && (
            <select
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
              className="px-3 py-2 rounded-lg border border-input bg-background text-sm"
            >
              <option value="">Todos os Tenants</option>
              {tenants.map((t) => (
                <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
              ))}
            </select>
          )}
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-2 rounded-lg border border-input bg-background text-sm"
          >
            {[2024, 2025, 2026, 2027].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <div className="rounded-xl border bg-gradient-to-br from-indigo-500 to-indigo-600 text-white p-4">
            <p className="text-2xl font-bold">{data.summary.total_km?.toLocaleString("pt-PT")}</p>
            <p className="text-xs opacity-80">Total KM</p>
          </div>
          <div className="rounded-xl border bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-4">
            <p className="text-2xl font-bold">{data.summary.reimbursable_km?.toLocaleString("pt-PT")}</p>
            <p className="text-xs opacity-80">KM Reembolsáveis</p>
          </div>
          <div className="rounded-xl border bg-gradient-to-br from-blue-500 to-blue-600 text-white p-4">
            <p className="text-2xl font-bold">{data.summary.total_trips?.toLocaleString("pt-PT")}</p>
            <p className="text-xs opacity-80">Total Viagens</p>
          </div>
          <div className="rounded-xl border bg-gradient-to-br from-purple-500 to-purple-600 text-white p-4">
            <p className="text-2xl font-bold">{data.summary.total_employees}</p>
            <p className="text-xs opacity-80">Colaboradores</p>
          </div>
          <div className="rounded-xl border bg-gradient-to-br from-amber-500 to-amber-600 text-white p-4">
            <p className="text-2xl font-bold">{data.summary.avg_km_per_trip?.toLocaleString("pt-PT")}</p>
            <p className="text-xs opacity-80">Média KM/Viagem</p>
          </div>
          <div className="rounded-xl border bg-card p-4">
            <p className="text-2xl font-bold text-emerald-600">{data.summary.approved}</p>
            <p className="text-xs text-muted-foreground">Aprovados</p>
          </div>
          <div className="rounded-xl border bg-card p-4">
            <p className="text-2xl font-bold text-amber-600">{data.summary.pending}</p>
            <p className="text-xs text-muted-foreground">Pendentes</p>
          </div>
        </div>
      )}

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly KM Chart */}
        <div className="rounded-xl border bg-card p-4">
          <h3 className="text-lg font-semibold mb-4">Quilómetros por Mês</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.monthly_chart || []}>
                <defs>
                  <linearGradient id="colorKm" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorReimb" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="month_name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="total_km" 
                  name="Total KM"
                  stroke="#4F46E5" 
                  fillOpacity={1} 
                  fill="url(#colorKm)" 
                />
                <Area 
                  type="monotone" 
                  dataKey="reimbursable_km" 
                  name="KM Reembolsáveis"
                  stroke="#10B981" 
                  fillOpacity={1} 
                  fill="url(#colorReimb)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Monthly Trips Chart */}
        <div className="rounded-xl border bg-card p-4">
          <h3 className="text-lg font-semibold mb-4">Viagens por Mês</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.monthly_chart || []}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="month_name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="trips" name="Viagens" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="approved" name="Aprovadas" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="pending" name="Pendentes" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Employees by KM */}
        <div className="lg:col-span-2 rounded-xl border bg-card p-4">
          <h3 className="text-lg font-semibold mb-4">Top 10 Colaboradores por KM</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={data?.employee_chart || []} 
                layout="vertical"
                margin={{ left: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(1)}k`} />
                <YAxis 
                  type="category" 
                  dataKey="employee_name" 
                  tick={{ fontSize: 11 }} 
                  width={100}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="total_km" name="Total KM" fill="#4F46E5" radius={[0, 4, 4, 0]} />
                <Bar dataKey="reimbursable_km" name="KM Reembolsáveis" fill="#10B981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Distribution Pie */}
        <div className="rounded-xl border bg-card p-4">
          <h3 className="text-lg font-semibold mb-4">Estado das Viagens</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-4 mt-2">
            {statusData.map((entry, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                <span className="text-sm">{entry.name}: {entry.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vehicle Usage */}
        <div className="rounded-xl border bg-card p-4">
          <h3 className="text-lg font-semibold mb-4">Utilização por Viatura</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.vehicle_chart || []}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="license_plate" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(1)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="total_km" name="Total KM" fill="#06B6D4" radius={[4, 4, 0, 0]}>
                  {(data?.vehicle_chart || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Employee Details Table */}
        <div className="rounded-xl border bg-card p-4">
          <h3 className="text-lg font-semibold mb-4">Detalhes por Colaborador</h3>
          <div className="overflow-auto max-h-72">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Colaborador</th>
                  <th className="px-3 py-2 text-right font-medium">KM</th>
                  <th className="px-3 py-2 text-right font-medium">Viagens</th>
                  <th className="px-3 py-2 text-right font-medium">Média</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(data?.employee_chart || []).map((emp, index) => (
                  <tr key={emp.employee_id} className="hover:bg-muted/30">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        ></div>
                        {emp.employee_name}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-medium">{emp.total_km?.toLocaleString("pt-PT")}</td>
                    <td className="px-3 py-2 text-right">{emp.trips}</td>
                    <td className="px-3 py-2 text-right text-muted-foreground">
                      {emp.trips > 0 ? Math.round(emp.total_km / emp.trips).toLocaleString("pt-PT") : 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Monthly Comparison */}
      <div className="rounded-xl border bg-card p-4">
        <h3 className="text-lg font-semibold mb-4">Evolução Mensal - Comparativo</h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data?.monthly_chart || []}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="month_name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="total_km" 
                name="Total KM"
                stroke="#4F46E5" 
                strokeWidth={2}
                dot={{ fill: "#4F46E5", strokeWidth: 2 }}
              />
              <Line 
                type="monotone" 
                dataKey="trips" 
                name="Viagens"
                stroke="#8B5CF6" 
                strokeWidth={2}
                dot={{ fill: "#8B5CF6", strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default KmDashboardPage;
